export const CURRENCY_SYMBOL = { USD: "$", CAD: "C$" };

export function money(value, currency = "USD") {
  if (value === null || value === undefined) return "—";
  const sym = CURRENCY_SYMBOL[currency] || "$";
  const neg = value < 0;
  const abs = Math.abs(value);
  const formatted = abs.toLocaleString("en-US", {
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  });
  return `${neg ? "-" : ""}${sym}${formatted}`;
}

export function hours(value) {
  if (value === null || value === undefined) return "—";
  return `${value.toLocaleString("en-US", { maximumFractionDigits: 1 })}h`;
}

// Time Tracker / reporting: always 2 decimal places (e.g. 30 min = 0.50h).
export function hours2(value) {
  if (value === null || value === undefined) return "—";
  return `${value.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}h`;
}

export function minToHours2(minutes) {
  if (minutes === null || minutes === undefined || minutes === "") return "—";
  return hours2(Number(minutes) / 60);
}

export function percent(value) {
  if (value === null || value === undefined) return "—";
  return `${value.toLocaleString("en-US", { maximumFractionDigits: 1 })}%`;
}

// ── Decision vocabulary ──────────────────────────────────────────────
// Brand Book: standalone decision badges show ONLY 4 states (Expand, Review,
// Improve, Stop). "Continue" is a positive engine state that must never appear
// as a vague standalone label — it surfaces only inside the AI Value Label.
export const DECISION = {
  expand: { label: "Expand", cls: "bg-[#1E9E6A]/10 text-[#1E9E6A] border-[#1E9E6A]/25", note: "AI is saving time, reducing cost, or creating value with enough confidence to use more." },
  review: { label: "Review", cls: "bg-gold/20 text-[#8a6d12] border-gold/50", note: "The result is unclear, incomplete, or needs more evidence before a confident decision." },
  improve: { label: "Improve", cls: "bg-editorial/10 text-editorial border-editorial/30", note: "AI could work, but the workflow, prompt, process, training, or tool choice needs fixing." },
  stop: { label: "Stop", cls: "bg-coral/15 text-coral border-coral/40", note: "AI is making the work slower, more expensive, lower quality, or clearly negative value." },
};

// Map any legacy/engine key onto the 4 standalone Brand Book states.
// "continue" folds into Expand here (positive → keep using) for standalone badges.
const DECISION_NORMALIZE = {
  scale: "expand", expand_pilot: "expand", expand: "expand", continue: "expand",
  monitor: "review", needs_more_evidence: "review", review: "review", watch: "review",
  review_process: "improve", improve: "improve", train: "improve",
  pause: "stop", negative_value: "stop", negative_roi: "stop", reduce: "stop", stop: "stop",
};

export function normalizeDecision(status) {
  return DECISION_NORMALIZE[status] || "review";
}

export function decisionInfo(status) {
  return DECISION[normalizeDecision(status)] || DECISION.review;
}

// AI Value Label context — allows the 5th positive "Continue" state to show.
export const DECISION_LABEL_STATES = {
  ...DECISION,
  continue: { label: "Continue", cls: "bg-aqua/25 text-[#1d6e72] border-aqua/50", note: "AI is working — keep going as it is." },
};

const DECISION_NORMALIZE_5 = {
  scale: "expand", expand_pilot: "expand", expand: "expand", continue: "continue",
  monitor: "review", needs_more_evidence: "review", review: "review", watch: "review",
  review_process: "improve", improve: "improve", train: "improve",
  pause: "stop", negative_value: "stop", negative_roi: "stop", reduce: "stop", stop: "stop",
};

export function decisionV2(status) {
  return DECISION_LABEL_STATES[DECISION_NORMALIZE_5[status] || "review"] || DECISION_LABEL_STATES.review;
}

export const EVIDENCE_TIER = {
  A: { label: "Verified", cls: "bg-gold/20 text-[#8a6d12] border-gold/50", note: "Randomised pilot / A-B" },
  B: { label: "High confidence", cls: "bg-aqua/20 text-[#1d6e72] border-aqua/50", note: "Staggered rollout" },
  C: { label: "Moderate confidence", cls: "bg-editorial/10 text-editorial border-editorial/30", note: "Historical / matched cohort" },
  D: { label: "Directional", cls: "bg-amber-50 text-amber-700 border-amber-200", note: "Manager standard" },
  E: { label: "Estimate only", cls: "bg-slate-100 text-slate-600 border-slate-200", note: "Staff estimate" },
};

export const BENEFIT_TYPE_LABEL = {
  hard_saving: "Hard saving",
  cost_avoidance: "Cost avoidance",
  revenue_gained: "Revenue gained",
  productivity_capacity_value: "Productivity capacity value",
  quality_risk_benefit: "Quality / risk benefit",
  strategic_option_value: "Strategic option value",
};

export const IMPACT_COLOR = {
  positive: "text-[#1E9E6A]",
  negative: "text-coral",
  neutral: "text-slate-500",
};

export const CHART_COLORS = {
  base: "#1D2F4F",
  aggressive: "#1E9E6A",
  conservative: "#F3C452",
  navy: "#1D2F4F",
  ink: "#1D2F4F",
  editorial: "#3F78D8",
  aqua: "#87DDE1",
  gold: "#F3C452",
  berry: "#B15B74",
  positive: "#1E9E6A",
  negative: "#FF7058",
};
